import React, { useCallback, useEffect, useState } from "react";
import { Layout } from "shared/utils";
import dayjs from "dayjs";
import "dayjs/locale/es";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import {
  Box,
  ClickAwayListener,
  IconButton,
  MenuItem,
  MenuList,
  Paper,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import styles from "./styles/autorizationPayments.module.scss";
import "../read-documents/styles/_menuFunctions.scss";
import "shared/components/paginator/styles/_filterSearchOrder.scss";
import tableStyles from "../read-documents/styles/tableResults.module.scss";
import { FormFilter } from "../../shared/components/form-filter/FormFilter";
import { accesRoles, Dispatch, Spinner } from "shared";
import { ButtonAction } from "shared/components";
import { useHookFormExportedPackages } from "./hooks";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { TableExportedPackages } from "./components/TableExportedPackages";
import { FormRevertExport } from "./components/ModalRevertExport";
import { columnsExportedPackages } from "./helper/columnsExportedPackages";
import { IExportedPackage } from "./types";
import { fieldsFormExportedPackages } from "./helper/fieldsFormExportedPackages";
import { ModalSeguimientoCuentas } from "./components/ModalSeguimientoCuentas";
import { ModalCargarInformacionPago } from "./components/ModalCargarInformacionPago";
import { getPagePaymentPackage, downloadPaymentPackage, downloadDraftResolution, getPaymentPackageStates, getDetailPaymentPackage } from "shared/api/services";
import { mapPaymentPackageToExportedPackage } from "./helper/mapperExportedPackages";
import { downloadDocument } from "shared/utils/helpers/downloadDocument";
import { getStateId, createStateIdMap } from "./helper/stateCodeMapper";
import { PaymentPackageState } from "shared/api/services";

export const ExportedInvoicePackages = () => {
  const [openOptions, setOpenOptions] = useState(false);
  const [hasContract, setHasContract] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [openSeguimiento, setOpenSeguimiento] = useState(false);
  const [packages, setPackages] = useState<IExportedPackage[]>([]);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(50);
  const [totalElements, setTotalElements] = useState(0);
  const [contentSize, setContentSize] = useState(0);
  const [filters, setFilters] = useState<any>(null);
  const [packageStates, setPackageStates] = useState<PaymentPackageState[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const handleToggleOptions = () => setOpenOptions((prev) => !prev);
  const handleCloseOptions = () => setOpenOptions(false);
  const handleOpenSeguimiento = () => {
    handleCloseOptions();
    setOpenSeguimiento(true);
  };
  const handleNavigateDeductions = () => {
    handleCloseOptions();
    navigate("/autorizaciones/gestion-deducciones");
  };
  const handleNavigateCostCenter = () => {
    handleCloseOptions();
    navigate("/autorizaciones/administrar-centro-costo");
  };

  useEffect(() => {
    dayjs.locale("es");
  }, []);

  const {
    handleSubmit,
    handleChange,
    getValueDatePickerStart,
    getValueDatePickerEnd,
    maxDate,
    onChangesDatePicker,
    values: filterValues,
    handleUserAssignment,
    handleClear,
  } = useHookFormExportedPackages(setShowForm, setFilters);

  const dispatch = useDispatch<Dispatch>();
  const navigate = useNavigate();

  const contractTabValue = hasContract ? "true" : "false";

  const handleToggleFilters = () => {
    setShowForm((prev) => !prev);
  };

  const handleTabsChange = (_event: React.SyntheticEvent, value: string) => {
    if (value !== contractTabValue) {
      setHasContract(value === "true");
    }
  };

  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    dispatch.menuDrawer.changeSelectedIndex(31);
  }, []);

  useEffect(() => {
    const fetchPackageStates = async () => {
      try {
        const states = await getPaymentPackageStates();
        setPackageStates(states);
      } catch (error) {
        console.error("Error fetching package states:", error);
      }
    };
    fetchPackageStates();
  }, []);

  const fetchPackages = useCallback(async () => {
    setIsLoading(true);
    try {
      const params: any = {
        page,
        size,
      };

      const stateIdMap = createStateIdMap(packageStates);

      if (filters) {
        if (filters.nit) params.employerIdNumber = filters.nit;
        if (filters.paymentConsecutive) params.consecutive = filters.paymentConsecutive;
        if (filters.exportStatus) params.packageStateId = getStateId(filters.exportStatus, stateIdMap);
        if (filters.exportDateRange?.start) {
          params.createdAtStart = dayjs(filters.exportDateRange.start).format("YYYY-MM-DD");
        }
        if (filters.exportDateRange?.end) {
          params.createdAtEnd = dayjs(filters.exportDateRange.end).format("YYYY-MM-DD");
        }
      }

      const response = await getPagePaymentPackage(params);
      const mappedPackages = response.content.map(mapPaymentPackageToExportedPackage);
      setPackages(mappedPackages);
      setTotalElements(response.totalElements);
      setContentSize(response.contentSize);
    } catch (error) {
      console.error("Error fetching packages:", error);
    } finally {
      setIsLoading(false);
    }
  }, [page, size, filters, packageStates]);

  useEffect(() => {
    if (!hasContract) {
      fetchPackages();
    }
  }, [page, size, hasContract, filters, fetchPackages]);

  const handleChangePage = (newPage: number) => {
    setPage(newPage - 1);
  };

  const handleChangeSize = (newSize: number) => {
    setSize(newSize);
    setPage(0);
  };

  const valuesResultsPages = [50, 100, 150, 200];

  const handleViewDetail = (packageData: IExportedPackage) => {
    navigate(`/autorizaciones/paquetes-exportados/${packageData.id}`, {
      state: {
        package: packageData,
      },
    });
  };

  const handleRevertExport = (packageData: IExportedPackage) => {
    const onClose = () => {
      dispatch.messageModal.cleanStateMessage();
    };

    const onSuccess = () => {
      handleCloseOptions();
      fetchPackages();
    };

    dispatch.messageModal.setMessageModal({
      show: true,
      title: "Revertir exporte de pago",
      onClose() {
        onClose();
      },
      size: "medium",
      description: React.createElement(FormRevertExport, {
        onClose,
        onSuccess,
        consecutive: packageData.paymentConsecutive,
      }),
    });
  };

  const handleDownloadZip = async (packageData: IExportedPackage) => {
    try {
      const response = await downloadPaymentPackage({
        consecutive: packageData.paymentConsecutive,
      });
      downloadDocument(
        response,
        "Descarga exitosa",
        "El archivo ZIP se ha descargado correctamente",
        "success"
      );
    } catch (error) {
      console.error("Error downloading ZIP:", error);
    }
  };

  const handleDownloadDraftResolution = async (packageData: IExportedPackage) => {
    try {
      const response = await downloadDraftResolution({
        consecutive: packageData.paymentConsecutive,
      });
      downloadDocument(
        response,
        "Descarga exitosa",
        "La preforma de resolución se ha descargado correctamente",
        "success"
      );
    } catch (error) {
      console.error("Error downloading draft resolution:", error);
    }
  };

  const handleLoadPaymentInfo = async (packageData: IExportedPackage) => {
    try {
      const detail = await getDetailPaymentPackage({
        consecutive: packageData.paymentConsecutive,
      });

      const onClose = () => {
        dispatch.messageModal.cleanStateMessage();
      };

      const onSuccess = () => {
        onClose();
        fetchPackages();
      };

      const budgetCertificates = detail.content
        .map((item: any) => item.budgetCertificate)
        .filter((cert: string) => cert)
        .join(", ");

      const budgetRegistrations = detail.content
        .map((item: any) => item.budgetRegistration)
        .filter((reg: string) => reg)
        .join(", ");

      const readOnlyData = {
        numeroFacturas: packageData.invoicesCount?.toString() || "",
        codigoPrestador: packageData.nit || "",
        valorPagado: packageData.totalExportValue?.toString() || "",
        numeroCDP: budgetCertificates,
        numeroRPC: budgetRegistrations,
      };

      dispatch.messageModal.setMessageModal({
        show: true,
        title: "Cargar información de pago",
        onClose() {
          onClose();
        },
        size: "1200px",
        description: React.createElement(ModalCargarInformacionPago, {
          onClose,
          onSuccess,
          packageData: packageData,
          readOnlyData,
          electronicDocumentIds: packageData.electronicDocumentIds,
        }),
      });
    } catch (error) {
      console.error("Error fetching package detail:", error);
    }
  };

  const columns = columnsExportedPackages({
    onViewDetail: handleViewDetail,
    onRevertExport: handleRevertExport,
    onDownloadZip: handleDownloadZip,
    onLoadPaymentInfo: handleLoadPaymentInfo,
    onDownloadDraftResolution: handleDownloadDraftResolution,
  });

  return (
    <Layout>
      <section className={styles.contentAutorizationPayment}>
        <Box className={`${styles.header} ${styles.headerWithBorder}`}>
          <Typography className="titleMainLayout">
            Paquetes facturas exportadas para pago
          </Typography>
          <Box className={styles.headerRight}>
            <Box className={styles.tabsAndSearch}>
              <Box
                className={`${tableStyles.searchContainer} ${styles.searchContainer}`}
              >
                <span
                  className={`${tableStyles.searchItems} ${styles.searchItems}`}
                >
                  <div
                    className={`padding-search ${styles.searchBox} ${
                      showForm ? styles.searchBoxActive : ""
                    }`}
                    role="button"
                    tabIndex={0}
                    onClick={handleToggleFilters}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        handleToggleFilters();
                      }
                    }}
                    data-testid="btn-filters-exported-packages"
                  >
                    <div className="contenido-autocomplete">
                      <SearchIcon className="icono-buscar" />
                      <TextField
                        className="inputText"
                        variant="outlined"
                        fullWidth
                        value={showForm ? "Ocultar filtros" : ""}
                        placeholder="Filtrar"
                        inputProps={{ readOnly: true }}
                        onFocus={(event) => event.target.blur()}
                        sx={{
                          "& .MuiInputBase-input": {
                            color:
                              "rgba(var(--general-color-r), var(--general-color-g), var(--general-color-b), 1)",
                            fontSize: "14px",
                          },
                          "& .MuiOutlinedInput-input::placeholder": {
                            color:
                              "rgba(var(--general-color-r), var(--general-color-g), var(--general-color-b), 1)",
                            opacity: 1,
                          },
                        }}
                      />
                    </div>
                  </div>
                </span>
              </Box>
              <Tabs
                value={contractTabValue}
                onChange={handleTabsChange}
                className={styles.contractTabs}
                aria-label="Filtro por contrato"
                data-testid="toogle-filter-contract-exported"
                variant="scrollable"
                scrollButtons
                allowScrollButtonsMobile
              >
                <Tab value="false" label="Sin contrato" disableRipple />
                <Tab value="true" label="Con contrato" disableRipple />
              </Tabs>
            </Box>
            <ClickAwayListener onClickAway={handleCloseOptions}>
              <Box className={styles.optionsWrapper}>
                <IconButton
                  onClick={handleToggleOptions}
                  sx={{
                    color:
                      "rgba(var(--general-color-r), var(--general-color-g), var(--general-color-b), 1)",
                  }}
                  data-testid="btn-options-exported-packages"
                >
                  <MoreVertIcon />
                </IconButton>
                {openOptions && (
                  <Paper
                    className="menuOptions"
                    elevation={3}
                    sx={{
                      position: "absolute",
                      right: 0,
                      top: "100%",
                      zIndex: 1300,
                      minWidth: 220,
                    }}
                  >
                    <MenuList className="MuiMenu-list">
                      <MenuItem sx={{ padding: 0 }}>
                        <ButtonAction
                          roles={accesRoles.auditor}
                          disabled={false}
                          action={handleOpenSeguimiento}
                        >
                          <span>Seguimiento de cuentas por provisionar</span>
                        </ButtonAction>
                      </MenuItem>
                      <MenuItem sx={{ padding: 0 }}>
                        <ButtonAction
                          roles={accesRoles.auditor}
                          disabled={false}
                          action={handleNavigateDeductions}
                        >
                          <span>Gestión deducciones</span>
                        </ButtonAction>
                      </MenuItem>
                      <MenuItem sx={{ padding: 0 }}>
                        <ButtonAction
                          roles={accesRoles.auditor}
                          disabled={false}
                          action={handleNavigateCostCenter}
                        >
                          <span>Administrar centro de costo</span>
                        </ButtonAction>
                      </MenuItem>
                    </MenuList>
                  </Paper>
                )}
              </Box>
            </ClickAwayListener>
          </Box>
        </Box>
        {showForm && (
          <FormFilter
            dataFields={fieldsFormExportedPackages(packageStates)}
            getValueDatePickerEnd={getValueDatePickerEnd}
            getValueDatePickerStart={getValueDatePickerStart}
            onChangesDatePicker={onChangesDatePicker}
            handleChange={handleChange}
            handleSubmit={handleSubmit}
            handleUserAssignment={handleUserAssignment}
            handleClear={handleClear}
            maxDate={maxDate}
            values={filterValues}
          />
        )}
        {!showForm && (
          <section className={styles.contentTable}>
            {hasContract ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  minHeight: "500px",
                }}
              >
                <Typography variant="h6" sx={{ color: "#6d6d6d" }}>
                  Información aun no disponible
                </Typography>
              </Box>
            ) : (
              <TableExportedPackages
                columns={columns}
                data={packages}
                valuesResultsPages={valuesResultsPages}
                page={page + 1}
                size={size}
                totalElements={totalElements}
                contentSize={contentSize}
                onChangePage={handleChangePage}
                onChangeSize={handleChangeSize}
              />
            )}
          </section>
        )}
        <ModalSeguimientoCuentas
          open={openSeguimiento}
          onClose={() => setOpenSeguimiento(false)}
        />
        {isLoading && <Spinner />}
      </section>
    </Layout>
  );
};
